import {
  getFromLocalStorageByKey,
  getHome,
  LoggerInstance,
  openPopup,
  TechnologyCategories,
} from '@vfit/shared/data-access';
import { IProduct } from '../api/products/products.models';
import {
  ICoverageToolFlowType,
  IGlobalConfigCMS,
  ITechCard,
  ITechnologiesFastwebCMS,
  ITechnologyFastwebCMS,
  ITechs,
} from '../cms';
import { Loader } from '@googlemaps/js-api-loader';
import { loaderObj } from '@vfit/shared/hooks';
import { QueryClient } from 'react-query';
import { IBBoxFWVF, IBlackBoxCMS, ICardBBoxFWVF } from '@vfit/consumer/components';
import { IBestTech, IOfferingServiceabilityResponse } from '@vfit/shared/models';
import { retrieveProduct } from './utils';
import { IProposalAction, IProposalCard } from '@vfit/consumer/providers';
import { organizeTechnology, recoverServiceabilityField } from './technology.utils';

const handleFWA = (linkFwa: string) => {
  if (linkFwa) openPopup(linkFwa);
};

const handleKo = (linkKO: string) => {
  if (linkKO) openPopup(linkKO);
};

const handleR2Logged = (linkR2: string) => {
  if (linkR2) openPopup(linkR2);
};

const getCoverageToolFlowType = (
  product: IProduct | undefined,
  globalConfig: IGlobalConfigCMS
): ICoverageToolFlowType => {
  if (!product) return 'STANDARD';
  if (product?.vffw?.isEnabled) {
    return product?.vffw?.coverageToolType || 'STANDARD';
  }
  if (globalConfig?.globalconfig?.vffw?.isEnabled === 'true') {
    return globalConfig?.globalconfig?.vffw?.coverageToolType || 'STANDARD';
  }
  return 'STANDARD';
};

const isHiddenVfFwFrontend = (
  product: IProduct | undefined,
  globalConfig: IGlobalConfigCMS
): boolean => {
  if (!product) return false;
  if (product?.vffw?.isEnabled) {
    return product?.vffw?.disableFrontend || false;
  }
  return globalConfig?.globalconfig?.vffw?.disableFrontend === 'true';
};

const redirectToShoppingCartFromReverseAddress = (cmsId: string) => {
  const url = `${getHome()}/shopping-cart?cmsId=${cmsId}&flowType=1`;
  if (url) {
    window.location.href = url;
  }
  return;
};

async function getAddressFromLatLng(lat: number, lng: number) {
  const mapsLoader = new Loader(loaderObj);
  const google = await mapsLoader.load();
  if (google !== undefined) {
    const geocoder = new google.maps.Geocoder();
    const latlng = new google.maps.LatLng(lat, lng);
    return new Promise((resolve, reject) => {
      geocoder.geocode(
        {
          location: latlng,
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            if (results !== null) {
              const filteredResults = results.filter(
                (result) => result.address_components.length === 8
              );
              if (filteredResults.length !== 0) {
                resolve(filteredResults[0]);
              } else resolve(results[0]);
            } else {
              LoggerInstance.debug('No results found');
            }
          } else {
            LoggerInstance.debug('Geocoder failed due to: ', status);
          }
          // eslint-disable-next-line prefer-promise-reject-errors
          reject('Geolocalization error');
        }
      );
    });
  }
  return null;
}

// region Logic Fastweb Vodafone

const convertMbToGb = (mbToGb: string) => {
  const mbit = parseFloat(mbToGb);
  if (isNaN(mbit)) {
    return mbToGb;
  }
  const gbit = mbit / 1000;
  if (gbit >= 1) {
    if (Number.isInteger(gbit)) {
      return `${gbit} Gbit/s`;
    } else {
      return `${parseFloat(gbit.toFixed(2)).toString()} Gbit/s`;
    }
  } else {
    return `${mbit} Mbit/s`;
  }
};

const organizeBestTechFromServiceAbility = (
  serviceAbility?: IOfferingServiceabilityResponse
): IBestTech => {
  const serviceAbilityData: IOfferingServiceabilityResponse =
    serviceAbility || getFromLocalStorageByKey('offeringServiceability');
  const findCharByKey = (id: string) => {
    const fEl = serviceAbilityData?.serviceabilityCharacteristic?.find(
      (e) => e?.displayName?.toLowerCase() === id?.toLowerCase()
    );
    return fEl?.displayValue || '';
  };
  return {
    checkSum: serviceAbilityData.buildingNumberChecksum || '',
    eagonId: serviceAbilityData?.serviceabilityAddress?.buildingNumber || '',
    bestTechValue: findCharByKey('BestTech'),
    BestTechTechnology: findCharByKey('BestTechTechnology'),
    bestTechMaxDownloadSpeed: findCharByKey('BestTechMaxDownloadSpeed'),
    bestTechMaxUploadSpeed: findCharByKey('BestTechMaxUploadSpeed'),
    bestTechMinDownloadVelocity: findCharByKey('BestTechMinDownloadVelocity'),
    bestTechMaxDownloadVelocity: findCharByKey('BestTechMaxDownloadVelocity'),
    bestTechMedDownloadVelocity: findCharByKey('BestTechMedDownloadVelocity'),
    bestTechMinUploadVelocity: findCharByKey('BestTechMinUploadVelocity'),
    bestTechMaxUploadVelocity: findCharByKey('BestTechMaxUploadVelocity'),
    bestTechMedUploadVelocity: findCharByKey('BestTechMedUploadVelocity'),
  };
};

const getFWTechByName = (
  technologiesFastwebCMS: ITechnologiesFastwebCMS,
  name?: string
): ITechnologyFastwebCMS | undefined => {
  let fWTech: ITechnologyFastwebCMS | undefined;
  if (!technologiesFastwebCMS || !name || Object?.keys(technologiesFastwebCMS)?.length === 0)
    return fWTech;
  Object.keys(technologiesFastwebCMS)?.forEach((technologyKey) => {
    const technology = technologiesFastwebCMS[technologyKey];
    if (technology?.technologyfastweb) {
      const allTechs = technology?.technologyfastweb?.mapping?.elements?.map((e) =>
        e?.value?.replace(/\s+/g, '').toLowerCase()
      );
      if (allTechs?.includes(name?.replace(/\s+/g, '').toLowerCase())) {
        fWTech = technology?.technologyfastweb;
      }
    }
  });
  return fWTech;
};

const getVFTechByName = (technologiesVodafoneCMS: ITechs, name?: string) => {
  let vfTech: ITechCard | undefined;
  if (!technologiesVodafoneCMS || !name || Object?.keys(technologiesVodafoneCMS)?.length === 0)
    return vfTech;
  switch (name) {
    case TechnologyCategories.FTTH:
      vfTech = technologiesVodafoneCMS.ftth;
      break;
    case TechnologyCategories.FTTC:
      vfTech = technologiesVodafoneCMS.fttc;
      break;
    case TechnologyCategories.ADSL:
      vfTech = technologiesVodafoneCMS.adsl;
      break;
    case TechnologyCategories.FWAINDOOR:
      vfTech = technologiesVodafoneCMS.FWAINDOOR;
      break;
    case TechnologyCategories.FWAOUTDOOR:
      vfTech = technologiesVodafoneCMS.FWAOUTDOOR;
      break;
    case TechnologyCategories.FWAOUTDOOR5G:
      vfTech = technologiesVodafoneCMS.FWAOUTDOOR5G;
      break;
    case TechnologyCategories.FTTHTIM:
      vfTech = technologiesVodafoneCMS.FTTHTIM;
      break;
    case TechnologyCategories.FTTHCD:
      vfTech = technologiesVodafoneCMS.FTTHCD;
      break;
    case TechnologyCategories.FWAINDOOR5G:
      vfTech = technologiesVodafoneCMS.FWAINDOOR5G;
      break;
    default:
      break;
  }
  return vfTech;
};

const getFirstAndSecondCard = (
  btb: IBBoxFWVF
): {
  firstCard: ICardBBoxFWVF;
  secondCard: ICardBBoxFWVF;
} => {
  const firstCard =
    btb?.cardVodafone?.position === '1'
      ? { ...btb?.cardVodafone, isVodafone: true }
      : { ...btb?.cardFastweb, isVodafone: false };
  const secondCard =
    btb?.cardVodafone?.position === '2'
      ? { ...btb?.cardVodafone, isVodafone: true }
      : { ...btb?.cardFastweb, isVodafone: false };
  return {
    firstCard,
    secondCard,
  };
};

const getBestTechConfigurations = (
  bestTechBlackBox: IBBoxFWVF,
  bestTech: IBestTech,
  downloadSpeedVodafone: string,
  foundedFastwebTechnology: ITechnologyFastwebCMS,
  foundedVodafoneTechnology: ITechCard,
  productVodafone: IProduct
) => {
  const cards = getFirstAndSecondCard(bestTechBlackBox);

  const purifyRawHtml = (rawHTML: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHTML, 'text/html');
    doc.querySelectorAll('[style]').forEach((el) => {
      if (el.tagName.toLowerCase() !== 'span') {
        el.removeAttribute('style');
      }
    });
    doc.querySelectorAll('br').forEach((el) => {
      el.replaceWith(document.createTextNode(' '));
    });
    return doc.body?.innerHTML || '';
  };

  const bolderizePriceForVodafoneProduct = (vodafonePrice: string): string => {
    const regex = /(\d{1,3}(?:[.,]\d{2})?€)/;
    return vodafonePrice.replace(regex, '<b>$1</b>');
  };

  const organizeCard = (type: 'firstCard' | 'secondCard') => {
    return {
      [type]: {
        isVodafone: cards?.[type]?.isVodafone || false,
        cornerColor: cards?.[type]?.isVodafone ? '#E60000' : '#FDC400',
        price: cards?.[type]?.isVodafone
          ? bolderizePriceForVodafoneProduct(productVodafone?.price?.toLowerCase() || '') || ''
          : foundedFastwebTechnology?.price?.price || '',
        recurrence: cards?.[type]?.isVodafone
          ? productVodafone?.recurrence
            ? `/${productVodafone?.recurrence?.toLowerCase()}`
            : ''
          : foundedFastwebTechnology?.price?.recurrence || '',
        subprice: cards?.[type]?.isVodafone
          ? purifyRawHtml(productVodafone?.description) || ''
          : foundedFastwebTechnology?.price?.subprice || '',
        cornerImage: cards?.[type]?.logo || '',
        items: cards?.[type]?.isVodafone
          ? foundedVodafoneTechnology?.technology?.vodafoneBlackBox?.map(
              (e) =>
                e.text?.replace('$MAXDOWNLOAD', convertMbToGb(downloadSpeedVodafone || '')) || ''
            )
          : foundedFastwebTechnology?.fastwebBlackBox?.elements?.map(
              (e) =>
                e.text?.replace(
                  '$MAXDOWNLOAD',
                  convertMbToGb(bestTech?.bestTechMaxDownloadSpeed || '')
                ) || ''
            ),
      },
      [`${type}TopLabel`]: cards?.[type]?.labelTopCard || '',
      [`${type}Action`]: {
        label: cards?.[type]?.labelButton || '',
        textColor: cards?.[type]?.isVodafone ? '#FFF' : '#0D0D0D',
        backgroundColor: cards?.[type]?.isVodafone ? '#E60000' : '#FDC400',
      },
    };
  };

  return {
    showBlackBox: true,
    enableCloseClickOutside: false,
    isShowClose: true,
    topLogo: bestTechBlackBox?.logoModal || '',
    title: bestTechBlackBox?.titleModal || '',
    blackboxTitle: bestTechBlackBox?.blackboxTitle || '',
    blackboxLiteTitle: bestTechBlackBox?.blackboxLiteTitle || '',
    blackboxDescription: bestTechBlackBox?.blackboxDescription || '',
    ...organizeCard('firstCard'),
    ...organizeCard('secondCard'),
    fastwebTechnology: foundedFastwebTechnology,
  };
};

const getBestTechFromCoverage = (
  client: QueryClient
): {
  showBlackBox: boolean;
  enableCloseClickOutside?: boolean;
  isShowClose?: boolean;
  topLogo?: string;
  title?: string;
  blackboxTitle?: string;
  blackboxLiteTitle?: string;
  blackboxDescription?: string;
  description?: string;
  firstCard?: IProposalCard;
  firstCardTopLabel?: string;
  firstCardAction?: IProposalAction;
  secondCard?: IProposalCard;
  secondCardTopLabel?: string;
  secondCardAction?: IProposalAction;
  fastwebTechnology?: ITechnologyFastwebCMS;
} => {
  const serviceAbilityData = getFromLocalStorageByKey('offeringServiceability');
  const product = retrieveProduct();
  const bestTech = organizeBestTechFromServiceAbility();
  const bestTechValue = bestTech?.bestTechValue || 'NA';
  const { blackbox } = (client?.getQueryData('getBestOfferVfFw') || {}) as IBlackBoxCMS;
  const technologiesVodafoneCMS = (client?.getQueryData('getCmsTecnologies') || {}) as ITechs;
  const technologiesFastwebCMS = (client?.getQueryData('getTechnologiesFastweb') ||
    {}) as ITechnologiesFastwebCMS;
  const organizedVodafoneTechnology = organizeTechnology(serviceAbilityData);
  const foundedVodafoneTechnology = getVFTechByName(
    technologiesVodafoneCMS,
    organizedVodafoneTechnology?.mappedCategory
  );
  const downloadSpeedVodafone =
    recoverServiceabilityField(
      serviceAbilityData?.serviceabilityCharacteristic,
      'MaxDownloadSpeed'
    ) || '-';
  const foundedFastwebTechnology = getFWTechByName(
    technologiesFastwebCMS,
    bestTech?.BestTechTechnology
  );

  let bestTechBBox;
  switch (bestTechValue) {
    case 'FW_C':
      bestTechBBox = blackbox?.fwC || {};
      return getBestTechConfigurations(
        bestTechBBox,
        bestTech,
        downloadSpeedVodafone,
        foundedFastwebTechnology,
        foundedVodafoneTechnology,
        product
      );
    case 'FW_A':
      bestTechBBox = blackbox?.fwA || {};
      return getBestTechConfigurations(
        bestTechBBox,
        bestTech,
        downloadSpeedVodafone,
        foundedFastwebTechnology,
        foundedVodafoneTechnology,
        product
      );
    // CASE FW_VF removed in 20/05
    // case 'FW_VF':
    //   bestTechBBox = blackbox?.fwVf || {};
    //   return getBestTechConfigurations(
    //     bestTechBBox,
    //     bestTech,
    //     downloadSpeedVodafone,
    //     foundedFastwebTechnology,
    //     foundedVodafoneTechnology,
    //     product
    //   );
    case 'FW_VF':
    case 'VF_C':
    case 'VF_A':
    case 'NA':
      return {
        showBlackBox: false,
      };
    default:
      return {
        showBlackBox: false,
      };
  }
};

// endregion

export {
  handleFWA,
  handleR2Logged,
  handleKo,
  getCoverageToolFlowType,
  isHiddenVfFwFrontend,
  redirectToShoppingCartFromReverseAddress,
  getAddressFromLatLng,
  convertMbToGb,
  organizeBestTechFromServiceAbility,
  getFWTechByName,
  getBestTechFromCoverage,
};
